import ListenerItem from '../../../../utils/ListenerItem';
import Validator from '../../../../utils/Validator';
import XtwTbl from '../../XtwTbl';

/** tooltip timeout in ms */
const TTP_TIMEOUT = 500;
/** tooltip space offset in pixels */
const SPACE_OFFS = 5;
/** matches unresolved statistics placeholders like ${sum} */
const PLACEHOLDER_PATTERN = /\$\{[^}]*\}/g;

/**
 * class XtwColTooltipExtension - provides the column header tooltip behavior for an
 * {@link XtwCol}. It is a plain delegate: the host column is passed in and accessed via
 * {@link #host}; the extension owns its own transient tooltip state (the tooltip span, the
 * remembered mouse position and the current sum).
 */
export default class XtwColTooltipExtension extends ListenerItem {

	/**
	 * constructs a new instance
	 * @param {*} host the host column (XtwCol)
	 */
	constructor( host ) {
		super( 'widgets.xtw.tooltip.XtwColTooltipExtension' );
		this._host = host;
		/** the current tooltip span element (if any) */
		this.spanTooltip = void 0;
		/** the currently displayed sum / resolved statistics tooltip (if any) */
		this.tooltipSum = void 0;
		/** remembered mouse coordinates for re-placing the span after a content change */
		this._tooltipMouseX = 0;
		this._tooltipMouseY = 0;
	}

	/**
	 * @returns {*} the host column
	 */
	get host() {
		return this._host;
	}

	/**
	 * the host column's DOM element. Exposed because this extension registers its own
	 * event listeners (via {@link ListenerItem#addListener}), which bind to this element.
	 * @returns {HTMLElement|undefined} the host's DOM element
	 */
	get element() {
		return this.host.element;
	}

	/**
	 * @returns {Boolean} true if the host column is rendered
	 */
	get isRendered() {
		return this.host.isRendered;
	}

	/**
	 * @override
	 */
	doDestroy() {
		this._destroyTooltipSpan();
		this._host = void 0;
		super.doDestroy();
	}

	/**
	 * @returns {String} the default tooltip
	 */
	get defaultTooltip() {
		return Validator.isString( this.host.ttip ) ? this.host.ttip : "";
	}

	/**
	 * the tooltip as presented to the user, with any yet unresolved statistics
	 * placeholders (e.g. ${sum}) removed. The placeholders are resolved by the
	 * backend on hover; until then they must not be shown to the user.
	 * @returns {String} the display tooltip
	 */
	get displayTooltip() {
		return this.defaultTooltip.replace( PLACEHOLDER_PATTERN, "" ).trim();
	}

	get tableEndX() {
		const table = this.host.xtdTbl;
		return table instanceof XtwTbl ? table.tableEndX : void 0;
	}

	/**
	 * @returns {DOMRectReadOnly | null} the area of the whole table or null if not available
	 */
	get tableRect() {
		const table = this.host.xtdTbl;
		return table instanceof XtwTbl ? table.tableRect : null;
	}

	/**
	 * @returns {DOMRectReadOnly | null} the area of the column header element or null if not available
	 */
	get columnHeadRect() {
		const element = this.host.element;
		if ( element instanceof HTMLElement ) {
			return element.getBoundingClientRect();
		}
		return null;
	}

	/**
	 * @return {DOMRectReadOnly | null} the area of the tooltip element or null if not available
	 */
	get spanTooltipRect() {
		if ( !( this.spanTooltip instanceof HTMLElement ) ) {
			return null;
		}
		return this.spanTooltip.getBoundingClientRect();
	}

	_setAsActiveSumColumn() {
		const table = this.host.xtdTbl;
		if ( table instanceof XtwTbl ) {
			table.activeMouseoverTooltipSumColumn = this.host;
			return true;
		}
		return false;
	}

	_removeAsActiveSumColumn() {
		const table = this.host.xtdTbl;
		if ( (table instanceof XtwTbl) && (table.activeMouseoverTooltipSumColumn === this.host) ) {
			table.activeMouseoverTooltipSumColumn = void 0;
			delete table.activeMouseoverTooltipSumColumn;
			return true;
		}
		return false;
	}

	addTooltipListeners() {
		// listeners are owned by this extension (its _onMouseEnter/_onMouseLeave) but bind
		// to the host column's element (see the element/isRendered getters above)
		const mouseEnterListenerAdded = this.addListener("mouseenter", "_onMouseEnter");
		const mouseLeaveListenerAdded = this.addListener("mouseleave", "_onMouseLeave");
		return mouseEnterListenerAdded && mouseLeaveListenerAdded;
	}

	removeTooltipListeners() {
		this.removeListener("mouseenter");
		this.removeListener("mouseleave");
	}

	/**
	 * "mouseenter" listener
	 * @param {MouseEvent} domEvent
	 */
	_onMouseEnter( domEvent ) {
		this._onMouseLeave();
		this._removeExistingTooltips();
		if ( domEvent.buttons > 0 ) {
			// a mouse button is pressed --> NO tooltip
			return;
		}
		const has_ttp = Validator.isString(this.defaultTooltip) || ((this.host.type === 1) && this.host.xtwHead.isColSumEnabled);
		if ( !has_ttp ) {
			// avoid showing empty tooltips
			return;
		}
		if ( this._createTooltipSpan(domEvent.clientX, domEvent.clientY) ) {
			const self = this;
			window.setTimeout( () => {
				self._showTooltipSpan();
			}, TTP_TIMEOUT );
		}
		this.tooltipSum = void 0;
		this._removeAsActiveSumColumn();
		if ( !this.host.select && (this.host.type === 1) ) {
			// numeric field - check for "column sum"
			this.host.notifyServer( "columnHover" );
		}
	}

	_removeExistingTooltips() {
		const table = this.host.xtdTbl;
		return table instanceof XtwTbl ? table.removeAllColumnTooltips() : false;
	}

	/**
	 * "mouseleave" listener
	 * @param {MouseEvent} domEvent
	 */
	_onMouseLeave( domEvent ) {
		this.tooltipSum = void 0;
		this._destroyTooltipSpan();
		this._removeAsActiveSumColumn();
	}

	/**
	 * creates the tooltip span element
	 * @param {Number} mouse_x current x coordinate of mouse cursor
	 * @param {Number} mouse_y current y coordinate of mouse cursor
	 * @returns {Boolean} true if successful; false otherwise
	 */
	_createTooltipSpan(mouse_x, mouse_y) {
		if ( !this.host.isRendered ) {
			return false;
		}
		this.host.element.title = "";
		// remember the mouse coordinates so the span can be re-placed later when
		// its content is updated (e.g. after the server delivered the statistics)
		this._tooltipMouseX = mouse_x;
		this._tooltipMouseY = mouse_y;
		const span = this.newSpanTooltip;
		this.spanTooltip = span;
		this.host.element.appendChild( this.spanTooltip );
		// place the span
		this._placeTooltipSpan(span, mouse_x, mouse_y);
		// make it invisible at this moment
		span.classList.add( "invisible" );
		return true;
	}

	/**
	 * re-measures and re-places the tooltip span. This is required whenever the
	 * span's content changes after it was initially placed: the initial placement
	 * freezes the span's width to fit the original (usually short) content, so
	 * new, wider content (e.g. a large sum) would otherwise be clipped/wrapped.
	 * @returns {Boolean} true if the span was re-placed
	 */
	_refreshTooltipSpanPlacement() {
		const span = this.spanTooltip;
		if ( !( span instanceof HTMLElement ) ) {
			return false;
		}
		// clear the previously frozen width so the span sizes to its new content
		span.style.width = "";
		this._placeTooltipSpan( span, this._tooltipMouseX, this._tooltipMouseY );
		return true;
	}

	/**
	 * places the tooltip element
	 * @param {HTMLElement} span the tooltip element
	 * @param {Number} mouse_x current x coordinate of mouse cursor
	 * @param {Number} mouse_y current y coordinate of mouse cursor
	 */
	_placeTooltipSpan(span, mouse_x, mouse_y) {
		const tbl_rect = this.tableRect;
		const col_rect = this.columnHeadRect;
		const ttp_rect = this.spanTooltipRect;
		if ( (tbl_rect instanceof DOMRectReadOnly) && (col_rect instanceof DOMRectReadOnly) && (ttp_rect instanceof DOMRectReadOnly) ) {
			const ttp_width = ttp_rect.width;
			const ttp_height = ttp_rect.height + SPACE_OFFS;
			let top = Math.max(col_rect.bottom + SPACE_OFFS, mouse_y + SPACE_OFFS);
			let left = (col_rect.left + col_rect.right) / 2  - ttp_width / 2;
			if ( (top + ttp_height) > tbl_rect.bottom ) {
				top = tbl_rect.top;
			}
			if ( left < tbl_rect.x ) {
				left = tbl_rect.x;
			}
			if ( (left + ttp_width) > tbl_rect.right ) {
				left = tbl_rect.right - ttp_width;
			}
			span.style.left = `${left}px`;
			span.style.width = `${ttp_width}px`;
			span.style.top = `${top}px`;
		} else {
			span.style.top = `${(mouse_y + SPACE_OFFS)}px`;
			span.style.left = `${(mouse_x + SPACE_OFFS)}px`;
		}
	}

	/**
	 * shows the tooltip element
	 */
	_showTooltipSpan() {
		if ( !this.host.isRendered || !( this.spanTooltip instanceof HTMLElement ) ) {
			return;
		}
		this.spanTooltip.classList.remove("invisible");
	}

	/**
	 * destroys the tooltip element
	 * @returns {Boolean} true if successful
	 */
	_destroyTooltipSpan() {
		if ( this.host.isRendered ) {
			this.host.element.title = this.displayTooltip;
		}
		let element = this.spanTooltip;
		this.spanTooltip = void 0;
		if ( !( element instanceof HTMLElement ) ) {
			return true;
		}
		element.innerHTML = "";
		element.remove();
		element = void 0;
		return true;
	}

	get newSpanTooltip() {
		const spanTooltip = window.document.createElement( "span" );
		spanTooltip.classList.add( "xtwheader-tooltip" );
		spanTooltip.role = "tooltip";
		spanTooltip.innerHTML = this.displayTooltip;
		const main_span = this.host.mainSpan;
		if ( main_span instanceof HTMLElement ) {
			spanTooltip.style.fontFamily = main_span.style.fontFamily;
			spanTooltip.style.fontSize = main_span.style.fontSize;
			spanTooltip.style.fontWeight = main_span.style.fontWeight;
		}
		return spanTooltip;
	}

	_addToSpanTooltip( addition ) {
		if ( !( this.spanTooltip instanceof HTMLElement ) ) {
			return false;
		}
		let ttp = this.displayTooltip;
		if ( Validator.isString(addition) ) {
			if ( Validator.isString(ttp) ) {
				ttp += '<br/>';
			}
			ttp += addition;
		}
		this.spanTooltip.innerHTML = ttp;
		// the content grew (e.g. a large sum was appended) - re-fit the span
		this._refreshTooltipSpanPlacement();
		return true;
	}

	/**
	 * replaces the tooltip's content with a tooltip whose statistics placeholders
	 * have already been resolved by the backend
	 * @param {String} resolvedTooltip the tooltip with all placeholders resolved
	 * @returns {Boolean} true if successful
	 */
	_setSpanTooltip( resolvedTooltip ) {
		if ( !( this.spanTooltip instanceof HTMLElement ) ) {
			return false;
		}
		let ttp = Validator.isString( resolvedTooltip ) ? resolvedTooltip : "";
		// the tooltip is rendered via innerHTML, where a real newline character
		// (ASCII 10) collapses to whitespace; turn newlines into explicit line
		// breaks so a "\n" in the source tooltip becomes a real line break
		ttp = ttp.replace( /\r\n|\r|\n/g, '<br/>' );
		this.spanTooltip.innerHTML = ttp;
		// the content changed (e.g. resolved statistics) - re-fit the span
		this._refreshTooltipSpanPlacement();
		return true;
	}

	/**
	 * changes the tooltip text of the column header
	 * @param {String} newTooltipValue new column tooltip
	 */
	setTooltip( newTooltipValue ) {
		if ( !Validator.isString( newTooltipValue ) ) {
			return false;
		}
		if ( this.defaultTooltip !== newTooltipValue ) {
			this.host.ttip = newTooltipValue;
			if ( this.host.element instanceof HTMLElement ) {
				this.host.element.title = this.displayTooltip;
			}
		}
		return true;
	}

	addSumToTooltip( sum ) {
		if ( this.host.type !== 1 ) { // only if the type is equal to 1, then it is a numeric field
			return false;
		}
		if ( Validator.isString(sum) ) {
			this.tooltipSum = sum;
			this._setAsActiveSumColumn();
			return this._addToSpanTooltip( 'Σ = ' + sum );
		} else {
			this.tooltipSum = null;
			if ( !Validator.isString(this.defaultTooltip) ) {
				this._onMouseLeave();
			}
			return this._addToSpanTooltip('');
		}
	}

	/**
	 * replaces the tooltip with one whose statistics placeholders (${sum},
	 * ${average}, ${median}, ${minimum}, ${maximum}, ${count}, ...) have already
	 * been resolved by the backend
	 * @param {String} resolvedTooltip the resolved tooltip
	 * @returns {Boolean} true if successful
	 */
	setStatisticsTooltip( resolvedTooltip ) {
		if ( this.host.type !== 1 ) { // only if the type is equal to 1, then it is a numeric field
			return false;
		}
		if ( !Validator.isString( resolvedTooltip ) ) {
			this.tooltipSum = null;
			return this._setSpanTooltip( this.displayTooltip );
		}
		// keep the resolved tooltip available for the "copy to clipboard" request
		this.tooltipSum = resolvedTooltip;
		this._setAsActiveSumColumn();
		return this._setSpanTooltip( resolvedTooltip );
	}

	handleCopyTooltipSumRequest() {
		if ( this.host.type !== 1 ) { // only if the type is equal to 1, then it is a numeric field
			return false;
		}
		this.host.notifyServer( "copyTooltipSum", { tooltipSum: this.tooltipSum } );
		return true;
	}

	makeSureTableIsFocused() {
		const table = this.host.xtdTbl;
		return table instanceof XtwTbl ? table.makeSureTableIsFocused() : false;
	}

}
